/**
 * GTM Analytics Event Functions
 *
 * This file contains typed wrapper functions for all analytics events.
 * Each function pushes an event to the GTM dataLayer with the appropriate
 * event name and typed properties.
 *
 * These functions maintain the same API as the previous Segment implementation
 * but use GTM's dataLayer instead.
 */

import { pushEvent } from './gtm';
import type {
  AccountCreated,
  AccountUpdated,
  AffirmClicked,
  CTAClicked,
  CartViewed,
  CheckoutStarted,
  CheckoutStepCompleted,
  CheckoutStepViewed,
  CookiePreferencesUpdated,
  CouponApplied,
  CouponDenied,
  CouponRemoved,
  DealerCalled,
  DealerClicked,
  DealerDirections,
  DealerListViewed,
  EmailClicked,
  EmailOpened,
  EmailSignupCompleted,
  EmailUnsubscribed,
  IDmeVerified,
  LinkClicked,
  NavigationClicked,
  NotifyMeClicked,
  NotifyMeCompleted,
  NotifyMeSend,
  NotifyMeSubmitted,
  OrderCompleted,
  PageViewed,
  PaymentInfoEntered,
  ProductAdded,
  ProductClicked,
  ProductListViewed,
  ProductRegistered,
  ProductRemoved,
  ProductReviewSubmitted,
  ProductReviewed,
  ProductViewed,
  PromotionClicked,
  PromotionViewed,
  RecipeListFiltered,
  RecipeListViewed,
  RecipeNoteAdded,
  RecipeNoteDeleted,
  RecipeNoteUpdated,
  RecipePrinted,
  RecipeReviewSubmitted,
  RecipeReviewed,
  RecipeShared,
  RecipeViewed,
  SearchSubmitted,
  SegmentConsentPreferenceUpdated,
  SignedIn,
  ViewedOrderDetails,
} from './types';

/**
 * Fires a 'Account Created' event
 */
export function accountCreated(props: AccountCreated): void {
  pushEvent('Account Created', props);
}

/**
 * Fires a 'Account Updated' event
 */
export function accountUpdated(props: AccountUpdated): void {
  pushEvent('Account Updated', props);
}

/**
 * Fires a 'Affirm Clicked' event
 */
export function affirmClicked(props: AffirmClicked): void {
  pushEvent('Affirm Clicked', props);
}

/**
 * Fires a 'CTA Clicked' event
 */
export function cTAClicked(props: CTAClicked): void {
  pushEvent('CTA Clicked', props);
}

/**
 * Fires a 'Cart Viewed' event
 */
export function cartViewed(props: CartViewed): void {
  pushEvent('Cart Viewed', props);
}

/**
 * Fires a 'Checkout Started' event
 */
export function checkoutStarted(props: CheckoutStarted): void {
  pushEvent('Checkout Started', props);
}

/**
 * Fires a 'Checkout Step Completed' event
 */
export function checkoutStepCompleted(props: CheckoutStepCompleted): void {
  pushEvent('Checkout Step Completed', props);
}

/**
 * Fires a 'Checkout Step Viewed' event
 */
export function checkoutStepViewed(props: CheckoutStepViewed): void {
  pushEvent('Checkout Step Viewed', props);
}

/**
 * Fires a 'Cookie Preferences Updated' event
 */
export function cookiePreferencesUpdated(props: CookiePreferencesUpdated): void {
  pushEvent('Cookie Preferences Updated', props);
}

/**
 * Fires a 'Coupon Applied' event
 */
export function couponApplied(props: CouponApplied): void {
  pushEvent('Coupon Applied', props);
}

/**
 * Fires a 'Coupon Denied' event
 */
export function couponDenied(props: CouponDenied): void {
  pushEvent('Coupon Denied', props);
}

/**
 * Fires a 'Coupon Removed' event
 */
export function couponRemoved(props: CouponRemoved): void {
  pushEvent('Coupon Removed', props);
}

/**
 * Fires a 'Dealer Called' event
 */
export function dealerCalled(props: DealerCalled): void {
  pushEvent('Dealer Called', props);
}

/**
 * Fires a 'Dealer Clicked' event
 */
export function dealerClicked(props: DealerClicked): void {
  pushEvent('Dealer Clicked', props);
}

/**
 * Fires a 'Dealer Directions' event
 */
export function dealerDirections(props: DealerDirections): void {
  pushEvent('Dealer Directions', props);
}

/**
 * Fires a 'Dealer List Viewed' event
 */
export function dealerListViewed(props: DealerListViewed): void {
  pushEvent('Dealer List Viewed', props);
}

/**
 * Fires a 'Email Clicked' event
 */
export function emailClicked(props: EmailClicked): void {
  pushEvent('Email Clicked', props);
}

/**
 * Fires a 'Email Opened' event
 */
export function emailOpened(props: EmailOpened): void {
  pushEvent('Email Opened', props);
}

/**
 * Fires a 'Email Signup Completed' event
 */
export function emailSignupCompleted(props: EmailSignupCompleted): void {
  pushEvent('Email Signup Completed', props);
}

/**
 * Fires a 'Email Unsubscribed' event
 */
export function emailUnsubscribed(props: EmailUnsubscribed): void {
  pushEvent('Email Unsubscribed', props);
}

/**
 * Fires a 'IDme Verified' event
 */
export function iDmeVerified(props: IDmeVerified): void {
  pushEvent('IDme Verified', props);
}

/**
 * Fires a 'Link Clicked' event
 */
export function linkClicked(props: LinkClicked): void {
  pushEvent('Link Clicked', props);
}

/**
 * Fires a 'Navigation Clicked' event
 */
export function navigationClicked(props: NavigationClicked): void {
  pushEvent('Navigation Clicked', props);
}

/**
 * Fires a 'Notify Me Clicked' event
 */
export function notifyMeClicked(props: NotifyMeClicked): void {
  pushEvent('Notify Me Clicked', props);
}

/**
 * Fires a 'Notify Me Completed' event
 */
export function notifyMeCompleted(props: NotifyMeCompleted): void {
  pushEvent('Notify Me Completed', props);
}

/**
 * Fires a 'Notify Me Send' event
 */
export function notifyMeSend(props: NotifyMeSend): void {
  pushEvent('Notify Me Send', props);
}

/**
 * Fires a 'Notify Me Submitted' event
 */
export function notifyMeSubmitted(props: NotifyMeSubmitted): void {
  pushEvent('Notify Me Submitted', props);
}

/**
 * Fires a 'Order Completed' event
 */
export function orderCompleted(props: OrderCompleted): void {
  pushEvent('Order Completed', props);
}

/**
 * Fires a 'Page Viewed' event
 */
export function pageViewed(props: PageViewed): void {
  pushEvent('Page Viewed', props);
}

/**
 * Fires a 'Payment Info Entered' event
 */
export function paymentInfoEntered(props: PaymentInfoEntered): void {
  pushEvent('Payment Info Entered', props);
}

/**
 * Fires a 'Product Added' event
 */
export function productAdded(props: ProductAdded): void {
  pushEvent('Product Added', props);
}

/**
 * Fires a 'Product Clicked' event
 */
export function productClicked(props: ProductClicked): void {
  pushEvent('Product Clicked', props);
}

/**
 * Fires a 'Product List Viewed' event
 */
export function productListViewed(props: ProductListViewed): void {
  pushEvent('Product List Viewed', props);
}

/**
 * Fires a 'Product Registered' event
 */
export function productRegistered(props: ProductRegistered): void {
  pushEvent('Product Registered', props);
}

/**
 * Fires a 'Product Removed' event
 */
export function productRemoved(props: ProductRemoved): void {
  pushEvent('Product Removed', props);
}

/**
 * Fires a 'Product Review Submitted' event
 */
export function productReviewSubmitted(props: ProductReviewSubmitted): void {
  pushEvent('Product Review Submitted', props);
}

/**
 * Fires a 'Product Reviewed' event
 */
export function productReviewed(props: ProductReviewed): void {
  pushEvent('Product Reviewed', props);
}

/**
 * Fires a 'Product Viewed' event
 */
export function productViewed(props: ProductViewed): void {
  pushEvent('Product Viewed', props);
}

/**
 * Fires a 'Promotion Clicked' event
 */
export function promotionClicked(props: PromotionClicked): void {
  pushEvent('Promotion Clicked', props);
}

/**
 * Fires a 'Promotion Viewed' event
 */
export function promotionViewed(props: PromotionViewed): void {
  pushEvent('Promotion Viewed', props);
}

/**
 * Fires a 'Recipe List Filtered' event
 */
export function recipeListFiltered(props: RecipeListFiltered): void {
  pushEvent('Recipe List Filtered', props);
}

/**
 * Fires a 'Recipe List Viewed' event
 */
export function recipeListViewed(props: RecipeListViewed): void {
  pushEvent('Recipe List Viewed', props);
}

/**
 * Fires a 'Recipe Note Added' event
 */
export function recipeNoteAdded(props: RecipeNoteAdded): void {
  pushEvent('Recipe Note Added', props);
}

/**
 * Fires a 'Recipe Note Deleted' event
 */
export function recipeNoteDeleted(props: RecipeNoteDeleted): void {
  pushEvent('Recipe Note Deleted', props);
}

/**
 * Fires a 'Recipe Note Updated' event
 */
export function recipeNoteUpdated(props: RecipeNoteUpdated): void {
  pushEvent('Recipe Note Updated', props);
}

/**
 * Fires a 'Recipe Printed' event
 */
export function recipePrinted(props: RecipePrinted): void {
  pushEvent('Recipe Printed', props);
}

/**
 * Fires a 'Recipe Review Submitted' event
 */
export function recipeReviewSubmitted(props: RecipeReviewSubmitted): void {
  pushEvent('Recipe Review Submitted', props);
}

/**
 * Fires a 'Recipe Reviewed' event
 */
export function recipeReviewed(props: RecipeReviewed): void {
  pushEvent('Recipe Reviewed', props);
}

/**
 * Fires a 'Recipe Shared' event
 */
export function recipeShared(props: RecipeShared): void {
  pushEvent('Recipe Shared', props);
}

/**
 * Fires a 'Recipe Viewed' event
 */
export function recipeViewed(props: RecipeViewed): void {
  pushEvent('Recipe Viewed', props);
}

/**
 * Fires a 'Search Submitted' event
 */
export function searchSubmitted(props: SearchSubmitted): void {
  pushEvent('Search Submitted', props);
}

/**
 * Fires a 'Segment Consent Preference Updated' event
 */
export function segmentConsentPreferenceUpdated(props: SegmentConsentPreferenceUpdated): void {
  pushEvent('Segment Consent Preference Updated', props);
}

/**
 * Fires a 'Signed In' event
 */
export function signedIn(props: SignedIn): void {
  pushEvent('Signed In', props);
}

/**
 * Fires a 'Viewed Order Details' event
 */
export function viewedOrderDetails(props: ViewedOrderDetails): void {
  pushEvent('Viewed Order Details', props);
}
